import { type ChangeEvent, useMemo } from 'react';

import { FormControl, FormHelperText } from '@mui/material';
import { DateField, type DateFieldProps } from '@mui/x-date-pickers-pro';
import {
  type Control,
  Controller,
  type FieldError,
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
} from 'react-hook-form';

import { useLocale } from '../../../src/hooks/locale';

import { RaLabel } from './RaLabel';

export type RaDateProps = Omit<
  DateFieldProps<Date | null>,
  'value' | 'onChange' | 'variant' | 'size' | 'InputProps' | 'name'
> & {
  control: Control<any>;
  name: string;
  label: string;
};

type RaDateFieldProps = Omit<RaDateProps, 'control' | 'name'> & {
  value: any;
  onChange: (
    event: ChangeEvent | FieldPathValue<FieldValues, FieldPath<FieldValues>>,
  ) => void;
  error?: FieldError;
};

const RaDateField: React.FC<RaDateFieldProps> = ({
  label,
  required = false,
  value,
  error,
  onChange,
  ...dateFieldProps
}) => {
  const dateValue = useMemo(() => {
    if (value == null) {
      return null;
    }

    try {
      // Date string without time, we have to set the time to 00:00:00 otherwise the date will have an hour shift because of timezone.
      if (typeof value === 'string' && !value.includes('T')) {
        return new Date(`${value}T00:00:00`);
      }

      return new Date(value);
    } catch (error) {
      console.error('date parsing error:', error);
      return new Date();
    }
  }, [value]);

  const handleChange = (date: Date | null) => {
    try {
      onChange(date?.toISOString() ?? null);
    } catch (error) {
      console.error('error', error);
    }
  };

  return (
    <FormControl fullWidth error={!!error}>
      <RaLabel label={label} required={required} />
      <DateField
        value={dateValue}
        onChange={handleChange}
        variant="outlined"
        size="small"
        InputProps={{
          sx: { background: 'white' },
        }}
        {...dateFieldProps}
      />
      <FormHelperText>{error?.message}</FormHelperText>
    </FormControl>
  );
};

export const RaDate: React.FC<RaDateProps> = ({
  control,
  name,
  label,
  required = false,
  ...dateFieldProps
}) => {
  const { t } = useLocale();
  const rules = {
    required: required ? t('This field is required') : undefined,
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ fieldState: { error }, field: { onChange, value } }) => (
        <RaDateField
          label={label}
          required={required}
          value={value}
          onChange={onChange}
          error={error}
          {...dateFieldProps}
        />
      )}
    />
  );
};
